import { useMemo, useState, type FormEvent } from 'react';
import { groupByItem, normalizeItemName, suggestItems, type ItemSuggestion } from '@tahi/core';
import { addItem, removeItem } from '../../../data/commands/shopping';
import { useCommandEnv } from '../../../data/hooks';
import type { PriceData } from '../../../data/repos/shopping';
import { Button, showToast } from '../../../ui';
import { copy, errorText } from '../copy';
import { numberText, quantityText } from '../format';
import { CARD, FOCUS, INPUT } from '../styles';
import { EMPTY_DRAFT, ItemDetailFields, parseDraft, type ItemDraft } from './ItemFields';

/** Quick add at the top of the list, with past items suggested as you type (spec §6). */
export function AddItemForm({ priceData }: { priceData: PriceData | undefined }) {
  const env = useCommandEnv();
  const [draft, setDraft] = useState<ItemDraft>(EMPTY_DRAFT);
  const [showDetails, setShowDetails] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const suggestions = useMemo<ItemSuggestion[]>(() => {
    if (!priceData || picked === draft.name) return [];
    return suggestItems(draft.name, priceData.lines, priceData.points);
  }, [priceData, draft.name, picked]);

  const lastPrice = useMemo(() => {
    const key = normalizeItemName(draft.name);
    if (!priceData || key === '') return null;
    const pts = groupByItem(priceData.points).get(key);
    return pts ? pts[pts.length - 1]!.priceMinor : null;
  }, [priceData, draft.name]);

  const patch = (p: Partial<ItemDraft>) => {
    setError(null);
    setDraft((d) => ({ ...d, ...p }));
  };

  function pick(s: ItemSuggestion) {
    setPicked(s.name);
    patch({
      name: s.name,
      quantity: draft.quantity || (s.lastQuantity == null ? '' : numberText(s.lastQuantity)),
      unit: draft.unit || (s.lastUnit ?? ''),
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = parseDraft(draft);
    if (!parsed.ok) {
      setError(errorText(parsed.error));
      return;
    }
    setBusy(true);
    try {
      const r = await addItem(env, parsed.input);
      if (!r.ok) {
        setError(errorText(r.error));
        return;
      }
      const name = parsed.input.name.trim();
      setDraft(EMPTY_DRAFT);
      setPicked(null);
      showToast({
        message: copy.added(name),
        action: { label: copy.undo, onPress: () => void removeItem(env, r.id) },
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className={`${CARD} flex flex-col gap-3`}>
      <div className="flex gap-2">
        <input
          aria-label={copy.addLabel}
          placeholder={copy.addPlaceholder}
          value={draft.name}
          maxLength={80}
          autoComplete="off"
          onChange={(e) => patch({ name: e.target.value })}
          className={`${INPUT} min-w-0 flex-1`}
        />
        <Button type="submit" variant="primary" disabled={busy || draft.name.trim() === ''}>
          {copy.add}
        </Button>
      </div>
      {suggestions.length > 0 ? (
        <ul aria-label={copy.suggestions} className="flex flex-col">
          {suggestions.map((s) => (
            <li key={s.normalizedName}>
              <button
                type="button"
                onClick={() => pick(s)}
                className={`flex min-h-11 w-full items-center justify-between gap-3 rounded-xl px-2 text-left ${FOCUS}`}
              >
                <span className="min-w-0 truncate text-fg">
                  {s.name}
                  {s.lastQuantity != null ? (
                    <span className="text-muted">
                      {' '}
                      · {quantityText(s.lastQuantity, s.lastUnit)}
                    </span>
                  ) : null}
                </span>
                {s.lastPriceMinor != null ? (
                  <span className="shrink-0 font-mono text-sm tabular-nums text-muted">
                    {copy.suggestionLast(s.lastPriceMinor)}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <button
        type="button"
        aria-expanded={showDetails}
        onClick={() => setShowDetails((v) => !v)}
        className={`min-h-11 self-start text-sm text-primary ${FOCUS}`}
      >
        {showDetails ? copy.fewerDetails : copy.moreDetails}
      </button>
      {showDetails ? (
        <ItemDetailFields
          draft={draft}
          onChange={patch}
          estimateHint={
            lastPrice != null && draft.estimate === '' ? copy.estimateHint(lastPrice) : null
          }
        />
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </form>
  );
}
